import { useMemo, useState } from 'react';
import { navigate } from '../../app/navigation';
import { areaCompletionFor, displayAddress, isInspectionPurged, isInspectionWorkable, type V2Inspection } from '../../domain/inspection';
import { RELEASE_LIMIT } from '../../../supabase/functions/_shared/billingPolicy';
import { useT, type Translator } from '../../i18n/i18n';
import { billingStore, coverageOfInspection, type ReleaseResult } from '../../state/billingStore';
import { useStore } from '../../state/createStore';
import { deleteInspectionAction } from '../../state/deleteInspection';
import { findingStore } from '../../state/findingStore';
import { inspectionProgress, inspectionStore } from '../../state/inspectionStore';
import { inspectionIssueCount } from '../../state/issueCounts';
import { ActionSheet, Button, Card, ConfirmSheet, Icon, IconButton, ProgressBar, SectionEyebrow, showToast, TopBar, type ActionSheetItem } from '../../ui/components';
import { formatClock, formatDate } from '../../ui/hooks';
import { CoverPhotoTile } from './CoverPhotoTile';
import { DraftRetentionBanners } from './DraftRetentionBanners';
import { ImportInspectionButton } from './ImportInspectionButton';
import { clientOf, inspectionStageOf, localityOf, StageBadge, streetOf, type InspectionStage } from './inspectionSummary';
import { MoveOutSheet } from './MoveOutSheet';
import { OtherDevicesBanner } from './OtherDevicesBanner';

// What deleting an unfinished inspection did to what paid for it (Owner 2026-10-20, billingPolicy.ts V-1).
function releaseMessage(release: ReleaseResult, t: Translator['t']): string | null {
  if (release.kind === 'released') return release.coverage === 'trial' ? t('release.released.trial') : t('release.released.single');
  if (release.kind === 'queued') return t('release.queued');
  if (release.kind === 'kept' && release.reason === 'aiUsed') return t('release.kept.aiUsed');
  if (release.kind === 'kept' && release.reason === 'limitReached') return t('release.kept.limitReached', { limit: RELEASE_LIMIT });
  return null;
}

// Inspections tab root — every inspection on this device. Body = Figma "01 — Inspections Home"
// (iPhone design frozen 2026-09-10): the active inspection card (street · locality · client ·
// scheduled time · item progress · Resume) · Schedule / New Inspection · Recent History. Since the
// Owner added the Home tab (2026-09-11) this page carries the ordinary tab top bar, not the brand
// header. The content stays the product's own: real stages, no sync claims (the records live on
// this device), and the options menu keeps the guarded delete. Nothing here forces the inspector
// into the creation form.
export function MyInspectionsScreen() {
  const { t, tp } = useT();
  const state = useStore(inspectionStore, (s) => s);
  const findingState = useStore(findingStore, (s) => s);
  const inspections = state.inspections; // sorted by updatedAt desc (inspectionStore)
  const stages = useMemo(() => new Map(inspections.map((i) => [i.id, inspectionStageOf(i, state, findingState)] as const)), [inspections, state, findingState]);
  // A copy exported to another device is never the one to resume here (Owner 2026-10-20).
  const current = inspections.find((i) => stages.get(i.id) !== 'reportComplete' && stages.get(i.id) !== 'movedOut') ?? null;
  const recent = inspections.filter((i) => i !== current);
  const [options, setOptions] = useState<V2Inspection | null>(null);
  const [pendingDelete, setPendingDelete] = useState<V2Inspection | null>(null);
  const [moving, setMoving] = useState<V2Inspection | null>(null);
  const billing = useStore(billingStore, (s) => s);

  async function handleDelete() {
    if (!pendingDelete) return;
    const id = pendingDelete.id;
    setPendingDelete(null);
    const { release } = await deleteInspectionAction(id);
    const message = releaseMessage(release, t);
    if (message) showToast(message, release.kind === 'released' ? 'success' : 'info', 6000);
  }

  // The delete confirmation, as true for THIS inspection as it can be (OD-12 · moved out · what it gives back).
  function deleteBodyOf(inspection: V2Inspection): string {
    if (isInspectionPurged(inspection)) return t('inspections.deletePurgedBody');
    if (inspection.movedOut) return t('inspections.deleteMovedBody');
    const coverage = isInspectionWorkable(inspection) ? coverageOfInspection(inspection.id, billing) : null;
    if (coverage === 'single' || coverage === 'trial') return `${t('inspections.deleteBody')} ${t('inspections.deleteReleaseNote', { limit: RELEASE_LIMIT, left: billing.server?.releasesLeft ?? RELEASE_LIMIT })}`;
    return t('inspections.deleteBody');
  }

  function optionItems(target: V2Inspection): ActionSheetItem[] {
    const items: ActionSheetItem[] = [
      {
        key: 'open',
        label: t('common.open'),
        icon: 'grid_view',
        onSelect: () => {
          setOptions(null);
          open(target);
        },
        testId: 'inspection-option-open'
      }
    ];
    // Owner 2026-10-20: carry an unfinished inspection to another device in a file.
    if (isInspectionWorkable(target)) {
      items.push({
        key: 'move',
        label: t('inspections.moveOut'),
        icon: 'drive_file_move',
        onSelect: () => {
          setMoving(target);
          setOptions(null);
        },
        testId: 'inspection-option-move'
      });
    }
    items.push({
      key: 'delete',
      label: target.movedOut ? t('moved.deleteCopy') : t('inspections.delete'),
      icon: 'delete',
      tone: 'danger',
      onSelect: () => {
        setPendingDelete(target);
        setOptions(null);
      },
      testId: 'delete-inspection'
    });
    return items;
  }

  function open(inspection: V2Inspection) {
    navigate({ screen: 'inspection', inspectionId: inspection.id });
  }

  function newInspection() {
    navigate({ screen: 'newInspection' });
  }

  function lastEditedOf(inspection: V2Inspection): string {
    const editedToday = new Date(inspection.updatedAt).toDateString() === new Date().toDateString();
    return t('inspections.lastEdited', { time: editedToday ? formatClock(inspection.updatedAt) : formatDate(inspection.updatedAt) });
  }

  // Report Complete rows carry the day the final report was generated; every other row carries
  // the scheduled / actual inspection day.
  function rowSubtitleOf(inspection: V2Inspection, stage: InspectionStage): string {
    if (stage === 'movedOut' && inspection.movedOut) return `${t('inspections.clientLine', { name: clientOf(inspection, t) })} · ${t('inspections.movedLine', { date: formatDate(inspection.movedOut.at) })}`;
    const when =
      stage === 'reportComplete' && inspection.finalizedAt !== null ? t('inspections.finishedOn', { date: formatDate(inspection.finalizedAt) }) : formatDate(inspection.inspectionAt);
    return `${t('inspections.clientLine', { name: clientOf(inspection, t) })} · ${when}`;
  }

  return (
    <div className="screen home-screen" data-testid="inspections-home">
      <TopBar title={t('nav.inspections')} subtitle={inspections.length > 0 ? tp('inspections.onDevice', inspections.length) : undefined} />
      {inspections.length === 0 ? (
        <div className="screen-body home-body">
          <DraftRetentionBanners />
          <OtherDevicesBanner />
          <Card className="home-empty" testId="home-empty">
            <span className="icon-tile">
              <Icon name="add_home_work" />
            </span>
            <p className="home-empty-title">{t('inspections.emptyTitle')}</p>
            <p className="home-empty-body">{t('inspections.emptyBody')}</p>
            <Button icon="add" onClick={newInspection} testId="new-inspection">
              {t('inspections.new')}
            </Button>
            <ImportInspectionButton />
          </Card>
        </div>
      ) : (
        <div className="screen-body home-body">
          <DraftRetentionBanners />
          <OtherDevicesBanner />
          {current ? (
            <section className="home-section">
              <div className="home-section-head">
                <SectionEyebrow tone="primary">{t('inspections.current')}</SectionEyebrow>
                <p className="section-meta" data-testid="current-last-edited">
                  {lastEditedOf(current)}
                </p>
              </div>
              <CurrentInspectionCard inspection={current} stage={stages.get(current.id) ?? 'inProgress'} onOpen={() => open(current)} onOptions={() => setOptions(current)} />
            </section>
          ) : null}
          <div className="home-new-wrap">
            <Button variant="outline" icon="add_circle" onClick={newInspection} testId="new-inspection" className="home-new">
              {t('inspections.scheduleOrNew')}
            </Button>
            <ImportInspectionButton />
          </div>
          {recent.length > 0 ? (
            <section className="home-section" data-testid="inspection-list">
              <div className="home-section-head">
                <SectionEyebrow tone="primary">{t('inspections.recent')}</SectionEyebrow>
                <p className="section-meta">{tp('inspections.onDevice', inspections.length)}</p>
              </div>
              <div className="list home-recent-list">
                {recent.map((inspection) => {
                  const stage = stages.get(inspection.id) ?? 'inProgress';
                  // Figma 01 row: street + stage on the first line, the client · day line below at
                  // full width; the ⋮ options stay a second button so both remain ≥48px targets.
                  return (
                    <div key={inspection.id} className="list-row home-row" data-name={displayAddress(inspection, t('inspection.noAddress'))}>
                      <button type="button" className="list-row-hit home-row-hit" onClick={() => open(inspection)} data-testid="inspection-row">
                        <span className="home-row-head">
                          <span className="home-row-title">{streetOf(inspection, t('inspection.noAddress'))}</span>
                          <StageBadge stage={stage} />
                        </span>
                        <span className="home-row-subtitle">{rowSubtitleOf(inspection, stage)}</span>
                      </button>
                      <IconButton icon="more_vert" label={t('inspections.options')} onClick={() => setOptions(inspection)} tone="soft" className="list-row-menu" testId="inspection-menu" />
                    </div>
                  );
                })}
              </div>
            </section>
          ) : null}
        </div>
      )}

      {options ? (
        <ActionSheet
          title={displayAddress(options, t('inspection.noAddress'))}
          subtitle={t('inspections.options')}
          onClose={() => setOptions(null)}
          testId="inspection-options-sheet"
          items={optionItems(options)}
        />
      ) : null}

      {moving ? <MoveOutSheet inspection={moving} onClose={() => setMoving(null)} /> : null}

      {pendingDelete ? (
        <ConfirmSheet
          title={t('inspections.deleteTitle', { address: displayAddress(pendingDelete, t('inspection.noAddress')) })}
          // OD-12: once the working data is purged, the record IS the PDFs — the confirmation says so
          // instead of describing photos and findings that no longer exist.
          body={deleteBodyOf(pendingDelete)}
          confirmLabel={t('inspections.delete')}
          danger
          onConfirm={() => void handleDelete()}
          onCancel={() => setPendingDelete(null)}
        />
      ) : null}
    </div>
  );
}

// Figma 01 active card: street + stage + ⋮ · locality · Client / Scheduled facts · item progress ·
// Resume. The areas / findings line below the bar is the product's own field summary.
function CurrentInspectionCard(props: { inspection: V2Inspection; stage: InspectionStage; onOpen: () => void; onOptions: () => void }) {
  const { t, tp } = useT();
  const state = useStore(inspectionStore, (s) => s);
  const findingState = useStore(findingStore, (s) => s);
  const inspection = props.inspection;
  const progress = inspectionProgress(state, inspection.id);
  // B2 (2026-10-27): the same 问题记录 number as Home and the inspection overview.
  const issues = inspectionIssueCount(state, findingState, inspection.id);
  const completion = areaCompletionFor(state.areas.filter((a) => a.inspectionId === inspection.id));
  const locality = localityOf(inspection, t);
  return (
    <Card className="home-active" testId="current-inspection">
      <div className="home-active-head">
        <h2 className="home-active-title">{streetOf(inspection, t('inspection.noAddress'))}</h2>
        <div className="home-active-side">
          <StageBadge stage={props.stage} />
          <IconButton icon="more_vert" label={t('inspections.options')} onClick={props.onOptions} tone="soft" testId="current-inspection-menu" />
        </div>
      </div>
      {/* Owner 2026-09-14: the report cover photo sits on the right of the locality / facts block (the red
          box of the device screenshot); without one the tile is the capture entry. */}
      <div className="home-active-body">
        <div className="home-active-main">
          {locality.length > 0 ? (
            <p className="home-active-locality" data-testid="current-locality">
              {locality}
            </p>
          ) : null}
          <dl className="home-facts">
            <div className="home-fact">
              <dt>{t('inspections.clientLabel')}</dt>
              <dd data-testid="current-client">{clientOf(inspection, t)}</dd>
            </div>
            <div className="home-fact">
              <dt>{t('inspections.scheduledLabel')}</dt>
              <dd data-testid="current-scheduled">
                {formatDate(inspection.inspectionAt)} · {formatClock(inspection.inspectionAt)}
              </dd>
            </div>
          </dl>
        </div>
        <CoverPhotoTile inspection={inspection} testId="current-cover" />
      </div>
      <div className="home-progress">
        <div className="home-progress-head">
          <span>{t('inspections.progressLabel')}</span>
          <strong data-testid="current-progress-line">{tp('common.itemsInspected', progress.total, { done: progress.settled })}</strong>
        </div>
        <ProgressBar value={progress.settled} total={progress.total} />
        <p className="card-note">
          {tp('inspections.areasDone', completion.enabledAreas, { done: completion.completedAreas })}
          {issues > 0 ? ` · ${tp('common.findingsCount', issues)}` : ''}
        </p>
      </div>
      <Button onClick={props.onOpen} testId="resume-inspection">
        {t('inspections.resume')}
      </Button>
    </Card>
  );
}
